'use client';

import { 
  Users, Handshake, DollarSign, Clock, Search, Edit2, ChevronDown, 
  Plus, MessageCircle, Calendar, TimerReset, Banknote, CheckCircle2, Kanban
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import Image from 'next/image';
import styles from './CrmModule.module.css';

// Dados baseados no seu print real
const KANBAN_DATA = {
  col1: {
    id: 'novos', title: 'Entrada / Novos', color: '#3b82f6', count: 4, total: 'R$100.00',
    cards: [
      { id: 1, name: 'Fabrizio Deus Fiel.', phone: '5511996957266', value: 'R$100.00', sla: true, alert: 6, tag1: 'Atualizado Hoje', tag1Type: 'good', tag2: '7 dia(s)', tag2Type: 'warn', avatar: 'https://i.pravatar.cc/150?u=fabrizio' }
    ]
  },
  col2: {
    id: 'atendimento', title: 'Atendimento', color: '#eab308', count: 3, total: 'R$1,500.00',
    cards: [
      { id: 2, name: 'Diego Sousa Santos', phone: '5511987173872', value: '', sla: false, alert: null, tag1: 'Parado há 1 semana', tag1Type: 'bad', tag2: '7 dia(s)', tag2Type: 'warn', avatar: 'https://i.pravatar.cc/150?u=diego' },
      { id: 3, name: 'Aesir ERP Comercial', phone: '5511983493522', value: '', sla: false, alert: 1, tag1: 'Parado há 1 dia', tag1Type: 'bad', tag2: '7 dia(s)', tag2Type: 'warn', avatar: '/assets/images/logo.png' }
    ]
  },
  col3: {
    id: 'concluidos', title: 'Concluidos', color: '#22c55e', count: 1, total: 'R$1,500.00',
    cards: [
      { id: 4, name: 'Ismaildo', phone: '551928499339', value: 'R$1,500.00', sla: true, alert: 9, tag1: 'Parado há 1 dia', tag1Type: 'bad', tag2: '7 dia(s)', tag2Type: 'warn', avatar: 'https://i.pravatar.cc/150?u=ismaildo' }
    ]
  }
};

export function CrmModule({ reverse = false }: { reverse?: boolean }) {

  // Função auxiliar para renderizar os cards
  const renderCard = (card: any, isHidden = false) => (
    <div className={`${styles.kCard} ${card.sla ? styles.alert : ''}`} style={{ opacity: isHidden ? 0 : 1 }}>
      {card.alert && <div className={styles.kNotification}>!{card.alert}</div>}
      
      {card.sla && (
        <div className={styles.kSlaHeader}>
          <TimerReset size={12} /> SLA ESTOURADO
        </div>
      )}

      <div className={styles.kCardMain}>
        <div className={styles.kCardProfile}>
          {card.avatar.includes('logo') ? (
            <div style={{width: 36, height: 36, background: '#ea580c', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
               <Image src={card.avatar} alt="Avatar" width={20} height={20} />
            </div>
          ) : (
            <img src={card.avatar} alt="Avatar" className={styles.kAvatar} />
          )}
          
          <div>
            <span className={styles.kName}>{card.name}</span>
            <span className={styles.kPhone}><FaWhatsapp size={10}/> {card.phone}</span>
          </div>
        </div>
        {card.value && <div className={styles.kValue}>{card.value}</div>}
      </div>

      <div className={styles.kTags}>
        <span className={`${styles.kTag} ${card.tag1Type === 'good' ? styles.kTagGood : styles.kTagBad}`}>
          {card.tag1Type === 'good' ? <CheckCircle2 size={10}/> : <Clock size={10}/>} {card.tag1}
        </span>
        <span className={`${styles.kTag} ${styles.kTagWarn}`}>
          <Clock size={10}/> {card.tag2}
        </span>
      </div>

      <div className={styles.kCardFooter}>
        <div className={styles.kFooterIcon}>
          <Image src="/assets/images/logo.png" alt="User" width={14} height={14} style={{filter: 'brightness(0) invert(1)'}} />
        </div>
        <div className={styles.kActions}>
          <div className={styles.kActionBtn}><MessageCircle size={12} /></div>
          <div className={styles.kActionBtn}><Calendar size={12} /></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.moduleWrapper} style={{ flexDirection: reverse ? 'row-reverse' : 'row' }}>
      
      {/* ==============================================
          COLUNA ESQUERDA: TEXTO E ARGUMENTOS
          ============================================== */}
      <div className={styles.textContent}>
        <span className={styles.badge}>Aesir CRM</span>
        <h3 className={styles.title}>
          Gestão Visual de Vendas e SLA
        </h3>
        <p className={styles.description}>
          Onde o dinheiro não escapa. Acompanhe a temperatura do lead, o valor no funil e alertas automáticos de SLA para clientes que estão esperando resposta.
        </p>

        <div className={styles.featureList}>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Banknote size={16} color="#10b981" /></div>
            Funil com Valores Reais (Saiba quanto há na mesa).
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><TimerReset size={16} color="#ef4444" /></div>
            Alertas de SLA (Lead esfriou? O sistema avisa).
          </div>
          <div className={styles.featureItem}>
            <div className={styles.featureIcon}><Users size={16} color="#3b82f6" /></div>
            Atribuição automática de vendedores e canais.
          </div>
        </div>
      </div>

      {/* ==============================================
          COLUNA DIREITA: MOCKUP KANBAN (FIEL AO PRINT)
          ============================================== */}
      <div className={styles.mockupContainer}>
        <div className={styles.kanbanMockup}>
          
          {/* MOUSE FANTASMA (DRAG AND DROP ANIMATION) */}
          <div className={styles.ghostInteractionArea}>
            <div className={styles.ghostFakeCard}>
              {/* Cópia do Card do Fabrizio para simular o arrasto */}
              <div className={styles.kNotification}>!6</div>
              <div className={styles.kSlaHeader}><TimerReset size={12} /> SLA ESTOURADO</div>
              <div className={styles.kCardMain}>
                <div className={styles.kCardProfile}>
                  <img src="https://i.pravatar.cc/150?u=fabrizio" alt="Avatar" className={styles.kAvatar} />
                  <div>
                    <span className={styles.kName}>Fabrizio Deus Fiel.</span>
                    <span className={styles.kPhone}><FaWhatsapp size={10}/> 5511996957266</span>
                  </div>
                </div>
                <div className={styles.kValue}>R$100.00</div>
              </div>
              <div className={styles.kTags}>
                <span className={`${styles.kTag} ${styles.kTagGood}`}>Atualizado Hoje</span>
                <span className={`${styles.kTag} ${styles.kTagWarn}`}>7 dia(s)</span>
              </div>
              <div className={styles.kCardFooter}>
                <div className={styles.kFooterIcon}><Image src="/assets/images/logo.png" alt="User" width={14} height={14} style={{filter: 'brightness(0) invert(1)'}} /></div>
                <div className={styles.kActions}><div className={styles.kActionBtn}><MessageCircle size={12} /></div><div className={styles.kActionBtn}><Calendar size={12} /></div></div>
              </div>
            </div>
            
            <svg className={styles.ghostCursor} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.5 3L18.5 16L12 17L14.5 22L12 23L9.5 18L4 21L5.5 3Z" fill="#1e293b" stroke="white" strokeWidth="2" strokeLinejoin="round"/>
            </svg>
          </div>

          {/* HEADER DO KANBAN */}
          <div className={styles.kanbanTopBar}>
            <div className={styles.kTopLeft}>
              <div className={styles.kFunnelIcon}><Kanban size={20} /></div>
              <div className={styles.kFunnelTitle}>
                teste <Edit2 size={14} color="#94a3b8" />
              </div>
            </div>
            <div className={styles.kTopRight}>
              <div className={styles.kBtnOrange}><Plus size={14} /> Criar Funil</div>
              <div className={styles.kFilter}>Todo o Período <ChevronDown size={14}/></div>
              <div className={styles.kFilter}>Todos os Deptos <ChevronDown size={14}/></div>
            </div>
          </div>

          {/* BOARD DAS COLUNAS */}
          <div className={styles.kanbanBoard}>
            
            {/* Coluna 1 */}
            <div className={styles.kanbanColumn}>
              <div className={styles.kColHeader}>
                <div className={styles.kColTitleRow}>
                  <div className={styles.kColTitle}><div className={styles.kDot} style={{background: KANBAN_DATA.col1.color}}></div> {KANBAN_DATA.col1.title} <span className={styles.kColCount}>{KANBAN_DATA.col1.count}</span></div>
                </div>
                <div className={styles.kColMoney}><DollarSign size={12}/> {KANBAN_DATA.col1.total}</div>
              </div>
              <div className={styles.kCardList}>
                {/* O card real fica invisível (opacity 0) porque o Fantasma está segurando ele */}
                {renderCard(KANBAN_DATA.col1.cards[0], true)}
                <div className={styles.kAddBtn}><Plus size={14} /> Adicionar cliente</div>
              </div>
            </div>

            {/* Coluna 2 */}
            <div className={styles.kanbanColumn}>
              <div className={styles.kColHeader}>
                <div className={styles.kColTitleRow}>
                  <div className={styles.kColTitle}><div className={styles.kDot} style={{background: KANBAN_DATA.col2.color}}></div> {KANBAN_DATA.col2.title} <span className={styles.kColCount}>{KANBAN_DATA.col2.count}</span></div>
                </div>
                <div className={styles.kColMoney}><DollarSign size={12}/> {KANBAN_DATA.col2.total}</div>
              </div>
              <div className={styles.kCardList}>
                {renderCard(KANBAN_DATA.col2.cards[0])}
                {renderCard(KANBAN_DATA.col2.cards[1])}
                <div className={styles.kAddBtn}><Plus size={14} /> Adicionar cliente</div>
              </div>
            </div>

            {/* Coluna 3 */}
            <div className={styles.kanbanColumn}>
              <div className={styles.kColHeader}>
                <div className={styles.kColTitleRow}>
                  <div className={styles.kColTitle}><div className={styles.kDot} style={{background: KANBAN_DATA.col3.color}}></div> {KANBAN_DATA.col3.title} <span className={styles.kColCount}>{KANBAN_DATA.col3.count}</span></div>
                </div>
                <div className={styles.kColMoney}><DollarSign size={12}/> {KANBAN_DATA.col3.total}</div>
              </div>
              <div className={styles.kCardList}>
                {renderCard(KANBAN_DATA.col3.cards[0])}
                <div className={styles.kAddBtn}><Plus size={14} /> Adicionar cliente</div>
              </div>
            </div>

          </div>

        </div>
      </div>

    </div>
  );
}